'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface PortfolioProject {
  id: number;
  title: string;
  category: string;
  industry: string;
  material: string;
  processes: string[];
  description: string;
  image: string;
  alt: string;
  detailImage: string;
  detailAlt: string;
  specifications: {
    quantity: string;
    tolerance: string;
    finish: string;
    leadTime: string;
  };
}

const portfolioProjects: PortfolioProject[] = [
  {
    id: 1,
    title: 'Precision Electronics Enclosure',
    category: 'Fabrication',
    industry: 'Electronics',
    material: 'Aluminum 5052',
    processes: ['Fiber Laser Cutting', 'CNC Bending', 'Powder Coating'],
    description: 'Complex electronics enclosure with intricate cutouts and precise bends for telecommunications equipment.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1705f96b7-1768628392542.png',
    alt: 'Precision aluminum electronics enclosure with intricate cutouts and bent edges',
    detailImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1df28aee9-1767689498841.png',
    detailAlt: 'Completed precision aluminum enclosure with powder coated finish',
    specifications: {
      quantity: '500 units',
      tolerance: '±0.1mm',
      finish: 'RAL 7035 Powder Coat',
      leadTime: '2 weeks'
    }
  },
  {
    id: 2,
    title: 'Industrial Structural Brackets',
    category: 'Fabrication',
    industry: 'Industrial',
    material: 'Mild Steel A36',
    processes: ['Laser Cutting', 'Forming', 'Welding', 'Powder Coating'],
    description: 'Heavy-duty structural brackets for industrial equipment with reinforced design and high load capacity.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1509acafd-1766990360145.png',
    alt: 'Heavy duty steel structural bracket with multiple mounting holes',
    detailImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1796ef7c8-1768628392849.png',
    detailAlt: 'Finished structural bracket with powder coating',
    specifications: {
      quantity: '1000 units',
      tolerance: '±0.5mm',
      finish: 'Black Textured Powder Coat',
      leadTime: '3 weeks'
    }
  },
  {
    id: 3,
    title: 'Medical Device Component',
    category: 'Fabrication',
    industry: 'Medical',
    material: 'Stainless Steel 316L',
    processes: ['Precision Laser Cutting', 'Deburring', 'Passivation'],
    description: 'Biocompatible stainless steel component for medical diagnostic equipment with mirror finish and tight tolerances.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_11993546c-1765183445806.png',
    alt: 'Precision stainless steel medical device component with mirror finish',
    detailImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d47b702a-1768628393381.png',
    detailAlt: 'Stainless steel medical component with polished finish',
    specifications: {
      quantity: '250 units',
      tolerance: '±0.05mm',
      finish: 'Mirror Polish + Passivation',
      leadTime: '4 weeks'
    }
  },
  {
    id: 4,
    title: 'Architectural Decorative Panel',
    category: 'Fabrication',
    industry: 'Architectural',
    material: 'Copper C110',
    processes: ['Artistic Laser Cutting', 'Patina Finishing'],
    description: 'Custom decorative copper panel with intricate laser-cut pattern for high-end architectural installation.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1598ac2a2-1767940869772.png',
    alt: 'Decorative copper architectural panel with intricate laser cut pattern',
    detailImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_17fa5486b-1765702917592.png',
    detailAlt: 'Finished decorative copper panel with artistic pattern',
    specifications: {
      quantity: '50 panels',
      tolerance: '±0.2mm',
      finish: 'Natural Patina + Protective Coating',
      leadTime: '6 weeks'
    }
  },
  {
    id: 5,
    title: 'Semiconductor Process Chamber',
    category: 'Welding',
    industry: 'Semiconductor',
    material: 'Stainless Steel 316L',
    processes: ['TIG Welding', 'Orbital Welding', 'Electropolishing'],
    description: 'Ultra-clean welded chamber for semiconductor manufacturing with certified welds and electropolished finish.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b85a0eb2-1768628391855.png',
    alt: 'Precision welded semiconductor process chamber',
    detailImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_10559d657-1768628391967.png',
    detailAlt: 'Electropolished semiconductor chamber with certified welds',
    specifications: {
      quantity: '10 units',
      tolerance: '±0.03mm',
      finish: 'Electropolished Ra < 0.4μm',
      leadTime: '8 weeks'
    }
  },
  {
    id: 6,
    title: 'Custom Machinery Frame',
    category: 'Welding',
    industry: 'Industrial',
    material: 'Mild Steel A36',
    processes: ['MIG Welding', 'Stress Relief', 'Powder Coating'],
    description: 'Robust welded frame for custom machinery with stress-relieved welds and durable powder coat finish.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a9c078c0-1768628392858.png',
    alt: 'Heavy duty welded steel machinery frame',
    detailImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1509acafd-1766990360145.png',
    detailAlt: 'Completed machinery frame with powder coating',
    specifications: {
      quantity: '25 units',
      tolerance: '±1.0mm',
      finish: 'Industrial Gray Powder Coat',
      leadTime: '4 weeks'
    }
  }
];

const categories = ['All', 'Fabrication', 'Welding', 'Assembly', 'Coating'];
const industries = ['All', 'Electronics', 'Medical', 'Industrial', 'Architectural', 'Semiconductor'];

export default function PortfolioGallery() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [selectedProject, setSelectedProject] = useState<PortfolioProject | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  const filteredProjects = portfolioProjects.filter(project => {
    const categoryMatch = selectedCategory === 'All' || project.category === selectedCategory;
    const industryMatch = selectedIndustry === 'All' || project.industry === selectedIndustry;
    return categoryMatch && industryMatch;
  });

  const handleProjectClick = (project: PortfolioProject) => {
    if (!isHydrated) return;
    setSelectedProject(project);
  };

  const handleCloseModal = () => {
    if (!isHydrated) return;
    setSelectedProject(null);
  };

  return (
    <section className="py-14 lg:py-22 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="BriefcaseIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-cta font-semibold text-primary">Our Work</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-headline font-bold text-foreground mb-4">
            Project Portfolio
          </h1>
          <p className="text-lg text-muted-foreground font-body">
            Explore our diverse portfolio of precision manufacturing projects across multiple industries and applications.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-12 space-y-6">
          {/* Category Filter */}
          <div className="space-y-3">
            <div className="text-sm font-cta font-semibold text-foreground">Filter by Process</div>
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-5 py-2.5 rounded-lg font-cta font-semibold transition-all duration-base ${
                    selectedCategory === category
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Industry Filter */}
          <div className="space-y-3">
            <div className="text-sm font-cta font-semibold text-foreground">Filter by Industry</div>
            <div className="flex flex-wrap gap-3">
              {industries.map((industry) => (
                <button
                  key={industry}
                  onClick={() => setSelectedIndustry(industry)}
                  className={`px-5 py-2.5 rounded-lg font-cta font-semibold transition-all duration-base ${
                    selectedIndustry === industry
                      ? 'bg-secondary text-secondary-foreground shadow-md'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  }`}
                >
                  {industry}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-sm text-muted-foreground font-body">
          Showing {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project)}
              className="group bg-card rounded-xl overflow-hidden border border-border hover:shadow-xl transition-all duration-base cursor-pointer"
            >
              <div className="relative h-64 overflow-hidden">
                <AppImage
                  src={project.image}
                  alt={project.alt}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-base"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="px-3 py-1 bg-primary/90 text-primary-foreground text-xs font-cta font-semibold rounded-full">
                      {project.category}
                    </span>
                    <span className="px-3 py-1 bg-secondary/90 text-secondary-foreground text-xs font-cta font-semibold rounded-full">
                      {project.industry}
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6 space-y-3">
                <h3 className="text-xl font-headline font-bold text-foreground group-hover:text-primary transition-colors duration-base">
                  {project.title}
                </h3>
                <p className="text-sm text-muted-foreground font-body line-clamp-2">
                  {project.description}
                </p>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground font-body">
                  <Icon name="CubeIcon" size={16} variant="outline" />
                  <span>{project.material}</span>
                </div>
                <div className="flex items-center space-x-2 text-primary font-cta font-semibold text-sm">
                  <span>View Details</span>
                  <Icon name="ArrowRightIcon" size={16} variant="outline" className="group-hover:translate-x-1 transition-transform duration-base" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="FolderOpenIcon" size={32} variant="outline" className="text-muted-foreground" />
            </div>
            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
              No Projects Found
            </h3>
            <p className="text-muted-foreground font-body">
              Try adjusting your filters to see more projects.
            </p>
          </div>
        )}
      </div>

      {/* Project Detail Modal */}
      {isHydrated && selectedProject && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div
            className="bg-card rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-96">
              <AppImage
                src={selectedProject.detailImage}
                alt={selectedProject.detailAlt}
                className="w-full h-full object-cover"
              />
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all duration-base shadow-lg"
                aria-label="Close modal"
              >
                <Icon name="XMarkIcon" size={24} variant="outline" className="text-foreground" />
              </button>
            </div>
            <div className="p-8 space-y-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-cta font-semibold rounded-full">
                    {selectedProject.category}
                  </span>
                  <span className="px-3 py-1 bg-secondary/10 text-secondary text-xs font-cta font-semibold rounded-full">
                    {selectedProject.industry}
                  </span>
                </div>
                <h2 className="text-3xl font-headline font-bold text-foreground">
                  {selectedProject.title}
                </h2>
                <p className="text-lg text-muted-foreground font-body">
                  {selectedProject.description}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-cta font-semibold text-muted-foreground mb-2">Material</div>
                    <div className="text-base font-body text-foreground">{selectedProject.material}</div>
                  </div>
                  <div>
                    <div className="text-sm font-cta font-semibold text-muted-foreground mb-2">Processes</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.processes.map((process, index) => (
                        <span key={index} className="px-3 py-1 bg-muted text-foreground text-xs font-body rounded-full">
                          {process}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-cta font-semibold text-muted-foreground mb-2">Specifications</div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Quantity:</span>
                        <span className="text-sm text-foreground font-cta font-semibold">{selectedProject.specifications.quantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Tolerance:</span>
                        <span className="text-sm text-foreground font-cta font-semibold">{selectedProject.specifications.tolerance}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Finish:</span>
                        <span className="text-sm text-foreground font-cta font-semibold">{selectedProject.specifications.finish}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground font-body">Lead Time:</span>
                        <span className="text-sm text-foreground font-cta font-semibold">{selectedProject.specifications.leadTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}